import { matchRoutes } from 'react-router-dom'
import type { IRoutes } from '../interfaces/config'

/**
 * The two tokens a `checkoutRuleRoutes` entry may carry, and the only two.
 *
 * `IConfig.checkoutRuleRoutes` says its values are spelled the way
 * `routes[].route` spells them — `/:slug/:sessionId/checkout/delivery-date` —
 * and every route `defaultConfig` authors uses exactly these two names. A
 * third token is an authoring mistake rather than a shape to support, and
 * `resolveCheckoutRuleRoute` refuses it below instead of navigating to a path
 * with a colon still in it.
 */
const ROUTE_TOKENS = ['slug', 'sessionId'] as const

/**
 * A `:name` param, matched by name rather than by position.
 *
 * Used twice: once to substitute, once to detect what substitution left
 * behind. Matching the whole name is what keeps `:slugFoo` from being read as
 * `:slug` followed by `Foo` — a `split(':slug').join(value)` would have
 * produced `/acmeFoo/…`, a path that resolves to nothing and reports nothing.
 */
const ROUTE_PARAM = /:([A-Za-z0-9_]+)/g

/**
 * The same shape without the `g` flag, for the leftover check.
 *
 * A separate constant rather than reusing the one above: `RegExp.test` on a
 * global regex advances `lastIndex` and answers differently on the next call
 * with the same input. Two constants cost less than remembering to reset it.
 */
const ANY_ROUTE_PARAM = /:[A-Za-z0-9_]+/

/**
 * Turns one authored `checkoutRuleRoutes` entry into a path worth navigating
 * to, or `null`.
 *
 * ⚠️ **The authored value is a route *pattern*, not a path.** COM-950's
 * acceptance criteria describe handing it straight to `pushView`, and that
 * would navigate to the literal string `/:slug/:sessionId/…` and land the
 * shopper on `NotFound`. Nothing else in this repo substitutes route params —
 * `generatePath` and `replace(':` both return zero hits — so the substitution
 * is here.
 *
 * **Returns `null` rather than a half-built path**, on four conditions: no
 * entry authored, no slug or session id to substitute, a `:token` surviving
 * the substitution, or a result the authored table does not match. The caller
 * treats every one of them the same way — a toast and a Datadog error — so
 * they do not need telling apart, and a partial path is the one answer that
 * would strand the shopper somewhere real-looking.
 *
 * **`matchRoutes` rather than a string test**, copying `resolveExit` in
 * `templates/OrderConfirmation.tsx` and for its reason: it is the function
 * `<Routes>` matches the same table with, so this asks the question
 * react-router is about to answer. `endsWith` or a substring test also accepts
 * a route the path does not match, and the redirect then lands on `NotFound` —
 * which is worse than not redirecting, because the shopper loses the cart too.
 * `IConfig` types `routes` as present and `routes/index.tsx` still guards
 * `config.routes &&` before mapping it, so read it defensively here as well.
 *
 * ⚠️ **The substituted values are deliberately not URL-encoded**, matching the
 * six existing sites that build a `/${slug}/${sessionId}/…` path. A slug
 * carrying `%2f` therefore survives as `//evil.com`, and containment is the
 * caller's `navigate(path, { replace: true })` — `history.replace` calls
 * `replaceState` bare and the browser's same-origin check throws, where
 * `history.push` falls through to `window.location.assign` and navigates
 * cross-origin. `templates/OrderConfirmation.tsx` documents the mechanism in
 * full. Encoding here instead would break every storefront whose slug contains
 * a legitimately encoded character, and would leave the `push` hazard live for
 * the next caller.
 */
export const resolveCheckoutRuleRoute = (
  authored: string | undefined,
  routes: IRoutes[] | undefined,
  slug: string | undefined,
  sessionId: string | undefined,
): string | null => {
  if (!authored || !slug || !sessionId) return null

  const values: Record<(typeof ROUTE_TOKENS)[number], string> = {
    slug,
    sessionId,
  }

  /*
   * An unknown param is left as it was found, so the test below catches it.
   * `String.replace` does not rescan what a replacement function returns, so a
   * slug containing `:sessionId` cannot be substituted into a second time.
   */
  const path = authored.replace(ROUTE_PARAM, (token, name) =>
    name in values ? values[name as (typeof ROUTE_TOKENS)[number]] : token,
  )

  if (ANY_ROUTE_PARAM.test(path)) return null

  const authoredTable = routes?.map(({ route }) => ({ path: route })) ?? []

  return matchRoutes(authoredTable, path) ? path : null
}

/**
 * A path that leaves this origin. `//host` and `/\host` are both
 * protocol-relative to a browser.
 *
 * Lives beside the resolver rather than beside either caller, because the
 * hazard is the resolver's: the paragraph above says in as many words that the
 * substituted values are not URL-encoded, so a slug carrying `%2f` comes back
 * from `resolveCheckoutRuleRoute` as `//evil.com/...`.
 *
 * ⚠️ **Only a `push` caller needs it.** `history.replace` calls `replaceState`
 * bare and the browser's same-origin check throws, so COM-950's redirect is
 * contained by its navigation method. COM-1045's *Cambiar* row and COM-1046's
 * guided walk both push, and both test against this. A second copy of the
 * regex is a second place to drop it, which is why the two share one.
 */
export const ESCAPES_ORIGIN = /^\/[/\\]/

export default resolveCheckoutRuleRoute
